"use client";

import { useEffect } from "react";
import { create } from "zustand";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Store ────────────────────────────────────────────────────────────────────

type ToastType = "success" | "error" | "warning" | "info";

/** A button inside the toast. Dismisses the toast when pressed. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastStore {
  toasts: Toast[];
  add: (message: string, type?: ToastType, action?: ToastAction) => void;
  remove: (id: string) => void;
}

/** Long enough to read. */
const DISMISS_MS = 4000;
/**
 * Long enough to notice, read, and decide. A toast you are meant to click is
 * useless if it is gone before you look up from the sentence you were writing,
 * which is exactly when these fire.
 */
const DISMISS_WITH_ACTION_MS = 12000;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  add: (message, type = "info", action) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { id, message, type, action }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, action ? DISMISS_WITH_ACTION_MS : DISMISS_MS);
  },
  remove: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

// ── Public hook ───────────────────────────────────────────────────────────────

export function useToast() {
  const add = useToastStore((s) => s.add);
  return {
    toast: (message: string, type?: ToastType, action?: ToastAction) => add(message, type, action),
    success: (message: string, action?: ToastAction) => add(message, "success", action),
    error: (message: string) => add(message, "error"),
    warning: (message: string) => add(message, "warning"),
    info: (message: string, action?: ToastAction) => add(message, "info", action),
  };
}

// ── Container ─────────────────────────────────────────────────────────────────

const ICONS: Record<ToastType, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const COLORS: Record<ToastType, string> = {
  success: "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-700/50 dark:text-green-300",
  error: "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-300",
  warning: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700/50 dark:text-amber-300",
  info: "bg-background border-border text-foreground",
};

const ICON_COLORS: Record<ToastType, string> = {
  success: "text-green-500",
  error: "text-red-500",
  warning: "text-amber-500",
  info: "text-muted-foreground",
};

function ToastItem({ toast }: { toast: Toast }) {
  const remove = useToastStore((s) => s.remove);
  const Icon = ICONS[toast.type];

  useEffect(() => {
    return () => {};
  }, []);

  return (
    <div
      className={cn(
        "flex items-start gap-2.5 px-3.5 py-3 rounded-xl border shadow-lg text-sm max-w-sm animate-in slide-in-from-bottom-2 duration-200",
        COLORS[toast.type]
      )}
    >
      <Icon className={cn("w-4 h-4 mt-0.5 flex-shrink-0", ICON_COLORS[toast.type])} />
      <div className="flex-1 min-w-0">
        <p className="leading-snug">{toast.message}</p>
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick();
              remove(toast.id);
            }}
            data-testid="toast-action"
            className="mt-1.5 text-xs font-semibold underline underline-offset-2 hover:opacity-80"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={() => remove(toast.id)}
        className="p-0.5 rounded opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
}
